import React, { useEffect, useMemo, useRef, useState } from "react";
import "../PanelContainer.scss";
import { useReducer } from "react";
import { useCallback, useLayoutEffect } from "react";
import PanelOverlay from "../Renderers/PanelOverlay";
import { PanelCommonProps } from "config/panelConfig";
import { useAnalysisPage } from "@features/analysis/useAnalysisPage";
import { useBrowserPage } from "@features/browser/useBrowserPage";
import { usePanelContainer } from "../usePanelContainer";
import vtkPolyData from "@kitware/vtk.js/Common/DataModel/PolyData";

import { useBufferPool } from "../Renderers/vtkManagers";
import type { CPRContext } from "../Renderers/vtkManagers";
import SimpleContextMenu from "@components/ContextMenu/SimpleContextMenu";
import { useBranchInfo } from "@contexts/BranchContext";
import MarkerLine from "@features/view/MarkerLine";
import { EditNames } from "config/analysisConfig";
import { useConfig } from "config/useConfig";
import { normalizeLesionStackSpacingMm } from "config/config";
import { setServerCprImageAngleStartDegrees } from "utils/serverCaptureSendInfo";
import useAuth from "@hooks/Auth/useAuth";
import { setActiveCprBoundaryEditHistoryKey } from "utils/CPRUtilities/cprBoundaryEditHistory";
import { useCprPanelIdentity } from "./useCprPanelIdentity";
import { useCprPanelDimensions } from "./useCprPanelDimensions";
import { useCprRendererLifecycle } from "./useCprRendererLifecycle";
import { useCprInteractionEvents } from "./useCprInteractionEvents";
import {
  useCprPaintActorVisibility,
  useCprResetOnResize,
  useCprWindowLevelEffect,
} from "./useCprActorEffects";
import { useCprAnglePathRefresh } from "./useCprAnglePathRefresh";
import {
  useCprFaiInitialization,
  useCprInitialMarkerReset,
} from "./useCprInitialMarkers";
import { useCprDataRenderer } from "./useCprDataRenderer";
import { useCprRendererRegistration } from "./useCprRendererRegistration";
import { useCprAnnotationMenus } from "./useCprAnnotationMenus";
import type { CaptureSetterType } from "./cprPanelTypes";
import { BranchInfo } from "utils/FunctionUtilites";
import ImageLayerStack, { type ImageLayerStackWheelEvent } from "../ImageLayerStack";
import {
  getBranchImageResource,
  getCprCrossSectionImagesFromParameter,
  filterImageLayerVisibilityForAnalysisType,
  buildCprImagePrefetchPlan,
  isCprCrossSectionImageModeEnabled,
  normalizeAngleDeg,
  resolveCprImageResourceModeForAnalysisType,
  resolveImageLayersWithRevision,
  resolveCprFrameDefaultViewBox,
  selectNearestCprAngleFrame,
  type CprCrossSectionImages,
  type CprFrameViewBox,
  type CprMode,
  type ImageLayerKey,
} from "../cprImageResources";
import CprImageMarkerOverlay from "./CprImageMarkerOverlay";
import { registerRenderer, scheduleRender } from "../renderState";
import { getConfiguredCprCrossSectionImageMode } from "../cprImageModeConfig";
import {
  getEffectiveCprCrossSectionImageMode,
  shouldForceLegacyCprForFfrLumenEdit,
} from "../cprImageModeState";
import {
  isCprLocalOverlayFallbackEnabled,
  registerLocalOverlayImageContext,
  withLocalOverlayFallbackLayers,
} from "../localOverlayImageProvider";
import {
  iscprCrossSectionImagesBranchDeferred,
  loadcprCrossSectionImagesBranchResource,
  mergecprCrossSectionImagesBranchResource,
} from "../cprCrossSectionImages";
import {
  isServerCprFrontDataBranchDeferred,
  loadServerCprFrontDataBranchResourceOnce,
} from "../frontDataCprDebugLoader";
import {
  getLabelVolumeFromSeriesId,
  getVolumeFromSeriesId,
} from "../Renderers/CornerstoneManager";
import type { ImageWindowingPolicy } from "../imageWindowing";

const CPR_SERVER_FRONT_DATA_ANGLE_STEP = 5;
const CPR_IMAGE_WHEEL_ANGLE_FACTOR = 0.05;
const CPR_IMAGE_SCROLL_DRAG_ANGLE_FACTOR = 0.3;


function hasAorticCprBranch(
  imageManifest: CprCrossSectionImages | null | undefined,
  branchId: number | string | null | undefined,
) {
  if (branchId === null || branchId === undefined) return false;
  const branch = imageManifest?.branches?.[String(branchId)];
  if (!branch?.cpr) return false;
  return !!(
    branch.cpr.straightened?.angleFrames?.length ||
    branch.cpr.stretched?.angleFrames?.length ||
    branch.cpr.avc?.angleFrames?.length
  );
}

function resolveAorticCprBranchId({
  imageManifest,
  preferredBranchId,
}: {
  imageManifest: CprCrossSectionImages | null | undefined;
  preferredBranchId: number | string | null | undefined;
}) {
  const preferredNumber = Number(preferredBranchId);
  const candidates = [
    Number.isFinite(preferredNumber) ? preferredNumber : undefined,
    49,
    48,
    0,
  ].filter((value): value is number => value !== undefined);
  const found = candidates.find((candidate) =>
    hasAorticCprBranch(imageManifest, candidate),
  );
  return found ?? candidates[0] ?? 49;
}

function isImageManifestUsableForCurrentAnalysis({
  manifest,
  resourceMode,
  analysisType,
}: {
  manifest: CprCrossSectionImages | null | undefined;
  resourceMode: string | null | undefined;
  analysisType: string | null | undefined;
}) {
  if (!manifest) return false;
  if (resourceMode !== "server") return true;
  if (!manifest.revision?.startsWith("front-data:")) return true;
  if (!analysisType) return true;
  return manifest.revision.split(":").includes(analysisType);
}

function snapCprServerImageAngle(angleDeg: number) {
  return normalizeAngleDeg(
    Math.round(angleDeg / CPR_SERVER_FRONT_DATA_ANGLE_STEP) *
      CPR_SERVER_FRONT_DATA_ANGLE_STEP,
  );
}

function resolveLesionStackCprViewBox(
  frame: {
    width: number;
    height: number;
    centerlinePath?: Array<{ x: number; y: number }>;
  } | null | undefined,
  fallback: CprFrameViewBox | undefined,
): CprFrameViewBox | undefined {
  if (!frame?.centerlinePath?.length) return fallback;

  const points = frame.centerlinePath.filter(
    (point) => Number.isFinite(point.x) && Number.isFinite(point.y),
  );
  if (!points.length) return fallback;

  const frameWidth = Math.max(1, frame.width);
  const frameHeight = Math.max(1, frame.height);
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const minX = Math.max(0, Math.min(...xs));
  const maxX = Math.min(frameWidth, Math.max(...xs));
  const minY = Math.max(0, Math.min(...ys));
  const maxY = Math.min(frameHeight, Math.max(...ys));
  const pathWidth = Math.max(1, maxX - minX);
  const pathHeight = Math.max(1, maxY - minY);
  const paddingX = Math.max(80, frameWidth * 0.12, pathWidth * 0.75);
  const paddingY = Math.max(32, frameHeight * 0.025, pathHeight * 0.04);
  const targetWidth = Math.min(
    frameWidth,
    Math.max(pathWidth + paddingX * 2, Math.min(frameWidth, 220)),
  );
  const targetHeight = Math.min(
    frameHeight,
    Math.max(pathHeight + paddingY * 2, Math.min(frameHeight, 320)),
  );
  const centerX = (minX + maxX) * 0.5;
  const centerY = (minY + maxY) * 0.5;
  const x = Math.max(0, Math.min(frameWidth - targetWidth, centerX - targetWidth * 0.5));
  const y = Math.max(0, Math.min(frameHeight - targetHeight, centerY - targetHeight * 0.5));

  return {
    x,
    y,
    width: targetWidth,
    height: targetHeight,
  };
}

function alignCprViewBoxToFrameCenter(
  frame: {
    width: number;
    height: number;
  } | null | undefined,
  viewBox: CprFrameViewBox | undefined,
): CprFrameViewBox | undefined {
  if (!frame || !viewBox) return viewBox;

  const frameWidth = Math.max(1, frame.width);
  const frameHeight = Math.max(1, frame.height);
  const width = Math.max(1, Math.min(frameWidth, viewBox.width));
  const height = Math.max(1, Math.min(frameHeight, viewBox.height));
  const x = Math.max(0, (frameWidth - width) * 0.5);
  const y = Math.max(0, Math.min(frameHeight - height, viewBox.y));

  return {
    x,
    y,
    width,
    height,
  };
}

type FocusedStretchedCprViewBoxCache = {
  resetKey: string;
  viewBox?: CprFrameViewBox;
  anchorCenterlineIndex?: number;
  anchorPathFraction?: number;
};

type CprViewBoxAnchorFrame = {
  width: number;
  height: number;
  centerlinePath?: Array<{
    index: number;
    x: number;
    y: number;
    distanceMm?: number;
    sourceCenterlineIndex?: number;
  }>;
};

function clampCprViewBoxToFrame(
  frame: CprViewBoxAnchorFrame | null | undefined,
  viewBox: CprFrameViewBox | undefined,
): CprFrameViewBox | undefined {
  if (!frame || !viewBox) return viewBox;

  const frameWidth = Math.max(1, frame.width);
  const frameHeight = Math.max(1, frame.height);
  const width = Math.max(1, Math.min(frameWidth, viewBox.width));
  const height = Math.max(1, Math.min(frameHeight, viewBox.height));

  return {
    x: Math.max(0, Math.min(frameWidth - width, viewBox.x)),
    y: Math.max(0, Math.min(frameHeight - height, viewBox.y)),
    width,
    height,
  };
}

function getCprPathPointCenterlineIndex(point: {
  index?: number;
  sourceCenterlineIndex?: number;
}) {
  return Number.isFinite(point.sourceCenterlineIndex)
    ? point.sourceCenterlineIndex!
    : point.index;
}

function getFiniteCprPathPoints(frame: CprViewBoxAnchorFrame | null | undefined) {
  return (frame?.centerlinePath ?? []).filter(
    (point) => Number.isFinite(point.x) && Number.isFinite(point.y),
  );
}

function getCprPathFraction(
  points: ReturnType<typeof getFiniteCprPathPoints>,
  pointIndex: number,
) {
  if (points.length <= 1) return 0.5;

  const distances = points.map((point) => Number(point.distanceMm));
  if (distances.every((distance) => Number.isFinite(distance))) {
    const first = distances[0];
    const last = distances[distances.length - 1];
    if (Number.isFinite(first) && Number.isFinite(last) && first !== last) {
      return Math.max(
        0,
        Math.min(1, (distances[pointIndex] - first) / (last - first)),
      );
    }
  }

  return pointIndex / (points.length - 1);
}

function selectFocusedCprAnchorPoint(
  frame: CprViewBoxAnchorFrame | null | undefined,
) {
  const points = getFiniteCprPathPoints(frame);
  if (!points.length) return null;

  const distances = points.map((point) => Number(point.distanceMm));
  if (distances.every((distance) => Number.isFinite(distance))) {
    const minDistance = Math.min(...distances);
    const maxDistance = Math.max(...distances);
    const targetDistance = (minDistance + maxDistance) * 0.5;
    let bestIndex = 0;
    let bestDistance = Number.POSITIVE_INFINITY;
    distances.forEach((distance, index) => {
      const delta = Math.abs(distance - targetDistance);
      if (delta < bestDistance) {
        bestDistance = delta;
        bestIndex = index;
      }
    });
    return {
      point: points[bestIndex],
      pathFraction: getCprPathFraction(points, bestIndex),
    };
  }

  const centerlineIndexes = points
    .map(getCprPathPointCenterlineIndex)
    .filter((value): value is number => Number.isFinite(value));
  if (centerlineIndexes.length) {
    const minIndex = Math.min(...centerlineIndexes);
    const maxIndex = Math.max(...centerlineIndexes);
    const targetIndex = (minIndex + maxIndex) * 0.5;
    let bestIndex = 0;
    let bestDistance = Number.POSITIVE_INFINITY;
    points.forEach((point, index) => {
      const centerlineIndex = getCprPathPointCenterlineIndex(point);
      if (!Number.isFinite(centerlineIndex)) return;
      const delta = Math.abs(centerlineIndex! - targetIndex);
      if (delta < bestDistance) {
        bestDistance = delta;
        bestIndex = index;
      }
    });
    return {
      point: points[bestIndex],
      pathFraction: getCprPathFraction(points, bestIndex),
    };
  }

  const bestIndex = Math.floor((points.length - 1) * 0.5);
  return {
    point: points[bestIndex],
    pathFraction: getCprPathFraction(points, bestIndex),
  };
}

function findFocusedCprAnchorPoint(
  frame: CprViewBoxAnchorFrame | null | undefined,
  cache: FocusedStretchedCprViewBoxCache,
) {
  const points = getFiniteCprPathPoints(frame);
  if (!points.length) return null;

  const anchorCenterlineIndex = Number(cache.anchorCenterlineIndex);
  if (Number.isFinite(anchorCenterlineIndex)) {
    let bestIndex = 0;
    let bestDistance = Number.POSITIVE_INFINITY;
    points.forEach((point, index) => {
      const centerlineIndex = getCprPathPointCenterlineIndex(point);
      if (!Number.isFinite(centerlineIndex)) return;
      const delta = Math.abs(centerlineIndex! - anchorCenterlineIndex);
      if (delta < bestDistance) {
        bestDistance = delta;
        bestIndex = index;
      }
    });
    return points[bestIndex];
  }

  const pathFraction = Number.isFinite(cache.anchorPathFraction)
    ? Math.max(0, Math.min(1, cache.anchorPathFraction!))
    : 0.5;
  return points[Math.max(0, Math.min(points.length - 1, Math.round(pathFraction * (points.length - 1))))];
}

function centerCprViewBoxOnAnchorPoint(
  frame: CprViewBoxAnchorFrame | null | undefined,
  baseViewBox: CprFrameViewBox | undefined,
  anchorPoint: { x: number; y: number } | null | undefined,
): CprFrameViewBox | undefined {
  if (!frame || !baseViewBox || !anchorPoint) {
    return clampCprViewBoxToFrame(frame, baseViewBox);
  }

  const frameWidth = Math.max(1, frame.width);
  const frameHeight = Math.max(1, frame.height);
  const width = Math.max(1, Math.min(frameWidth, baseViewBox.width));
  const height = Math.max(1, Math.min(frameHeight, baseViewBox.height));

  return clampCprViewBoxToFrame(frame, {
    x: anchorPoint.x - width * 0.5,
    y: anchorPoint.y - height * 0.5,
    width,
    height,
  });
}

function resolveFocusedStretchedCprAnchoredViewBox(
  frame: CprViewBoxAnchorFrame | null | undefined,
  cache: FocusedStretchedCprViewBoxCache,
): CprFrameViewBox | undefined {
  return centerCprViewBoxOnAnchorPoint(
    frame,
    cache.viewBox,
    findFocusedCprAnchorPoint(frame, cache),
  );
}

function createFocusedStretchedCprViewBoxCache({
  resetKey,
  frame,
  viewBox,
}: {
  resetKey: string;
  frame: CprViewBoxAnchorFrame | null | undefined;
  viewBox: CprFrameViewBox | undefined;
}): FocusedStretchedCprViewBoxCache {
  const anchor = selectFocusedCprAnchorPoint(frame);
  const anchorCenterlineIndex = anchor
    ? getCprPathPointCenterlineIndex(anchor.point)
    : undefined;

  return {
    resetKey,
    viewBox,
    anchorCenterlineIndex: Number.isFinite(anchorCenterlineIndex)
      ? anchorCenterlineIndex
      : undefined,
    anchorPathFraction: anchor?.pathFraction,
  };
}

function shouldUseFocusedStretchedCprInitialViewBox(
  analysisType: string | null | undefined,
  mode: CprMode | null | undefined,
) {
  return (
    mode === "stretched" &&
    (analysisType === "plaque" ||
      analysisType === "stenosis" ||
      analysisType === "isr")
  );
}

const PLAQUE_CPR_STACK_CROSS_SECTION_COUNT = 7;
const PLAQUE_CPR_STACK_MIDDLE_COUNT = PLAQUE_CPR_STACK_CROSS_SECTION_COUNT - 2;
const PLAQUE_CPR_STACK_LENGTH_EPSILON_MM = 1e-3;
type LesionStackMarkerIndex = number | null;
type LesionStackSavedMiddle = {
  pickPoint: {
    index: number;
    position?: [number, number, number];
    normal?: [number, number, number];
    viewNormal?: [number, number, number];
  };
  proximalIndex: number;
  distalIndex: number;
};

function findNearestLengthIndex(
  lengthArray: ArrayLike<number>,
  targetLength: number,
  startIndex: number,
  endIndex: number,
) {
  const last = lengthArray.length - 1;
  const from = Math.max(0, Math.min(last, Math.min(startIndex, endIndex)));
  const to = Math.max(0, Math.min(last, Math.max(startIndex, endIndex)));
  let bestIndex = from;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (let index = from; index <= to; index += 1) {
    const length = Number(lengthArray[index]);
    if (!Number.isFinite(length)) continue;
    const distance = Math.abs(length - targetLength);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = index;
    }
  }

  return bestIndex;
}

function getLesionStackMiddleCapacity(
  proximalLength: number,
  distalLength: number,
  spacingMm: number,
) {
  const distance = Math.abs(distalLength - proximalLength);
  if (!Number.isFinite(distance)) return 0;
  return Math.max(
    0,
    Math.min(
      PLAQUE_CPR_STACK_MIDDLE_COUNT,
      Math.floor(
        (distance - PLAQUE_CPR_STACK_LENGTH_EPSILON_MM) /
          spacingMm,
      ),
    ),
  );
}

function isLesionStackLengthBetween(
  value: number,
  proximalLength: number,
  distalLength: number,
) {
  return proximalLength <= distalLength
    ? value > proximalLength && value < distalLength
    : value < proximalLength && value > distalLength;
}

function buildLesionStackMarkerIndexes({
  pickPoints,
  lengthArray,
  frontExpand,
  middleAnchorFullIndex,
  spacingMm,
}: {
  pickPoints: Array<{ index?: number }>;
  lengthArray: ArrayLike<number> | null | undefined;
  frontExpand: number;
  middleAnchorFullIndex?: number;
  spacingMm: number;
}): LesionStackMarkerIndex[] {
  const proximalFullIndex = Number(pickPoints[0]?.index);
  const storedMiddleFullIndex = Number(pickPoints[1]?.index);
  const distalFullIndex = Number(pickPoints[2]?.index);

  if (
    !lengthArray?.length ||
    !Number.isFinite(proximalFullIndex) ||
    !Number.isFinite(distalFullIndex)
  ) {
    return [];
  }

  const last = lengthArray.length - 1;
  const proximalLocalIndex = Math.max(
    0,
    Math.min(last, Math.round(proximalFullIndex - frontExpand)),
  );
  const distalLocalIndex = Math.max(
    0,
    Math.min(last, Math.round(distalFullIndex - frontExpand)),
  );
  const proximalLength = Number(lengthArray[proximalLocalIndex]);
  const distalLength = Number(lengthArray[distalLocalIndex]);

  if (!Number.isFinite(proximalLength) || !Number.isFinite(distalLength)) {
    return [
      Math.round(proximalFullIndex),
      ...Array.from({ length: PLAQUE_CPR_STACK_MIDDLE_COUNT }, () => null),
      Math.round(distalFullIndex),
    ];
  }

  const direction = distalLength >= proximalLength ? 1 : -1;
  const middleCapacity = getLesionStackMiddleCapacity(
    proximalLength,
    distalLength,
    spacingMm,
  );
  const indices: LesionStackMarkerIndex[] = [Math.round(proximalFullIndex)];

  if (middleCapacity <= 0) {
    indices.push(
      ...Array.from({ length: PLAQUE_CPR_STACK_MIDDLE_COUNT }, () => null),
    );
    indices.push(Math.round(distalFullIndex));
    return indices;
  }

  const fallbackAnchorLength =
    proximalLength + direction * spacingMm;
  const candidateAnchorFullIndex = Number.isFinite(middleAnchorFullIndex)
    ? Number(middleAnchorFullIndex)
    : storedMiddleFullIndex;
  const candidateAnchorLocalIndex = Math.max(
    0,
    Math.min(last, Math.round(candidateAnchorFullIndex - frontExpand)),
  );
  const candidateAnchorLength = Number(lengthArray[candidateAnchorLocalIndex]);
  let anchorLength = Number.isFinite(candidateAnchorLength)
    ? candidateAnchorLength
    : fallbackAnchorLength;
  const shouldUseFallbackAnchor =
    !isLesionStackLengthBetween(anchorLength, proximalLength, distalLength) ||
    (direction > 0
      ? anchorLength < fallbackAnchorLength
      : anchorLength > fallbackAnchorLength);
  if (shouldUseFallbackAnchor) {
    anchorLength = fallbackAnchorLength;
  }

  for (let slot = 0; slot < PLAQUE_CPR_STACK_MIDDLE_COUNT; slot += 1) {
    if (slot >= middleCapacity) {
      indices.push(null);
      continue;
    }
    const targetLength =
      anchorLength + direction * spacingMm * slot;
    if (
      !isLesionStackLengthBetween(targetLength, proximalLength, distalLength)
    ) {
      indices.push(null);
      continue;
    }
    const localIndex = findNearestLengthIndex(
      lengthArray,
      targetLength,
      proximalLocalIndex,
      distalLocalIndex,
    );
    indices.push(localIndex + frontExpand);
  }

  indices.push(Math.round(distalFullIndex));
  return indices;
}

function getLesionStackDefaultMiddleAnchorIndex({
  pickPoints,
  lengthArray,
  frontExpand,
  spacingMm,
}: {
  pickPoints: Array<{ index?: number }>;
  lengthArray: ArrayLike<number> | null | undefined;
  frontExpand: number;
  spacingMm: number;
}) {
  const indexes = buildLesionStackMarkerIndexes({
    pickPoints: [pickPoints[0], { index: Number.NaN }, pickPoints[2]],
    lengthArray,
    frontExpand,
    spacingMm,
  });
  return indexes[1] ?? undefined;
}

const CPRPanel: React.FC<PanelCommonProps> = ({
  className,
  panelConfig,
}: PanelCommonProps) => {
  const {
    switchConfigs,
    globalToolState,
    panelToolState,
    selectedTool,
    currentVOI,
    selectedLayoutConfig,
    updateWindowLevelWidth,
  } = useAnalysisPage();
  const {
    analysisType,
    panelStates,
    fullscreenPanelComponentKey,
    selectedPanelClassName,
    updateCPRState,
    updateSelectedPanelConfig,
  } = usePanelContainer();
  const { selectedSeriesId } = useBrowserPage();
  const { accessToken } = useAuth();
  const { bufferPool, contextPool, setBufferPool, setContextPool } =
    useBufferPool();
  const { branchIds, mainBranchIds } = useBranchInfo();
  const { config, setData } = useConfig();
  const lesionStackSpacingMm = normalizeLesionStackSpacingMm(
    config.lesionStackSpacingMm,
  );

  const [resetref, setresetref] = useState<boolean>(false);
  const [labelonoff, setlabelonoff] = useState<boolean>(false);
  const [gogo, setGogo] = useState<"STOP" | "GO" | "GOGO">("STOP");
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [externalFocusIndicator, setExternalFocusIndicator] = useState<{
    visible: boolean;
    branchId: number | null;
    index: number;
  }>({ visible: false, branchId: null, index: -1 });
  const [cprViewRevision, forceUpdateCprOverlay] = useReducer(
    (count: number) => count + 1,
    0,
  );
  const lesionStackSavedMiddleRef = useRef<Record<string, LesionStackSavedMiddle>>({});
  const activeLesionStackBranchRef = useRef<string | null>(null);

  const [mainIndex, mode, rendererId] = useCprPanelIdentity(className);
  const isLesionStackViewLayout =
    analysisType === "plaque" && selectedLayoutConfig.type === "lesionStackView";

  const ref = useRef<HTMLDivElement>(null);
  const context = useRef<CPRContext | null>(null);
  const refSetter = useRef<CaptureSetterType>();
  const focusedStretchedCprInitialViewBoxRef = useRef<FocusedStretchedCprViewBoxCache | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const panelState = panelStates["CPR"].coronary!;
  if (!panelState || !panelState.current) return null;

  const panelStateStateRef = useRef(panelState.state);
  useEffect(() => {
    panelStateStateRef.current = panelState.state;
  }, [panelState.state]);

  const analysisTypeKey = analysisType as string;
  const isAorticMode =
    analysisType === "aorticValveCalcium" || analysisTypeKey === "tavi";
  const aorticBranchId =
    analysisTypeKey === "tavi"
      ? (config.tavi_aorta_id ?? config.aorta_id)
      : config.aorta_id;
  const aorticStartIndex =
    analysisTypeKey === "tavi"
      ? (config.tavi_start_index ?? config.aorta_start_index)
      : config.aorta_start_index;
  const aorticCurrentIndex =
    analysisTypeKey === "tavi"
      ? (config.tavi_current_index ?? config.aorta_current_index)
      : config.aorta_current_index;
  const pool = selectedSeriesId ? bufferPool[selectedSeriesId] : undefined;

  const configuredCprCrossSectionImageMode =
    getConfiguredCprCrossSectionImageMode();
  const forceLegacyCprForFfrLumenEdit = shouldForceLegacyCprForFfrLumenEdit(
    analysisType,
    config.EditMode,
  );
  const cprCrossSectionImageMode = getEffectiveCprCrossSectionImageMode(
    configuredCprCrossSectionImageMode,
    pool,
    !forceLegacyCprForFfrLumenEdit,
  );
  const isImageModeEnabled = isCprCrossSectionImageModeEnabled(
    cprCrossSectionImageMode,
  );
  const useServerLesionStackHiddenMiddleSync =
    isImageModeEnabled && cprCrossSectionImageMode === "server";
  const useServerLesionStackHiddenMiddleSyncRef = useRef(
    useServerLesionStackHiddenMiddleSync,
  );
  useEffect(() => {
    useServerLesionStackHiddenMiddleSyncRef.current =
      useServerLesionStackHiddenMiddleSync;
  }, [useServerLesionStackHiddenMiddleSync]);
  const [locallyMergedImageManifest, setLocallyMergedImageManifest] =
    useState<CprCrossSectionImages | null>(null);

  const pooledImageManifest = useMemo(() => {
    if (!isImageModeEnabled) return null;
    const candidate =
      getCprCrossSectionImagesFromParameter(pool?.cprCrossSectionImages?.data) ??
      getCprCrossSectionImagesFromParameter(pool?.parameter?.data);
    return isImageManifestUsableForCurrentAnalysis({
      manifest: candidate,
      resourceMode: cprCrossSectionImageMode,
      analysisType: analysisTypeKey,
    })
      ? candidate
      : null;
  }, [
    analysisTypeKey,
    cprCrossSectionImageMode,
    isImageModeEnabled,
    pool?.cprCrossSectionImages?.data,
    pool?.parameter?.data,
  ]);

  useEffect(() => {
    setLocallyMergedImageManifest((previous) => {
      if (!pooledImageManifest) return null;
      if (!previous || previous.revision !== pooledImageManifest.revision) {
        return pooledImageManifest;
      }

      let changed = false;
      const mergedBranches = { ...pooledImageManifest.branches };
      Object.entries(previous.branches ?? {}).forEach(([branchKey, previousBranch]) => {
        const pooledBranch = pooledImageManifest.branches?.[branchKey];
        if (previousBranch?.loaded === true && pooledBranch?.loaded !== true) {
          mergedBranches[branchKey] = {
            ...pooledBranch,
            ...previousBranch,
          };
          changed = true;
        }
      });

      return changed
        ? {
            ...pooledImageManifest,
            branches: mergedBranches,
          }
        : pooledImageManifest;
    });
  }, [pooledImageManifest]);

  const imageManifest = locallyMergedImageManifest ?? pooledImageManifest;

  const branchId = isAorticMode
    ? resolveAorticCprBranchId({
        imageManifest,
        preferredBranchId: aorticBranchId,
      })
    : panelState.current[mainIndex];

  const info = panelState.state[branchId] ?? [];
  useEffect(() => {
    if (
      !isImageModeEnabled ||
      !imageManifest ||
      !selectedSeriesId ||
      !accessToken ||
      !analysisType ||
      branchId === undefined
    ) {
      return;
    }

    const shouldLoadFrontDataBranch = isServerCprFrontDataBranchDeferred(
      imageManifest,
      branchId,
    );
    const shouldLoadManifestBranch = iscprCrossSectionImagesBranchDeferred(
      imageManifest,
      branchId,
    );

    if (!shouldLoadFrontDataBranch && !shouldLoadManifestBranch) return;

    const loadBranch = shouldLoadFrontDataBranch
      ? loadServerCprFrontDataBranchResourceOnce({
          accessToken,
          seriesId: selectedSeriesId,
          manifest: imageManifest,
          branchId,
          analysisType,
        })
      : loadcprCrossSectionImagesBranchResource({
          accessToken,
          seriesId: selectedSeriesId,
          manifest: imageManifest,
          branchId,
          CacheControl: {
            Pragma: "no-cache",
            Expires: "0",
          },
        });

    loadBranch
      .then(({ branchId: loadedBranchId, branch }) => {
        if (!isMountedRef.current) return;

        // Make the newly loaded branch renderable in this panel immediately.
        // Some datasets keep the root manifest under parameter.data and only
        // create cprCrossSectionImages after the deferred branch load completes;
        // relying only on the buffer-pool update can leave the first branch
        // switch stuck on the fallback UI until the user switches away/back.
        setLocallyMergedImageManifest((previous) => {
          const baseManifest =
            previous?.revision === imageManifest.revision ? previous : imageManifest;
          const branchKey = String(loadedBranchId);
          if (baseManifest.branches?.[branchKey]?.loaded === true) {
            return previous === baseManifest ? previous : baseManifest;
          }
          return mergecprCrossSectionImagesBranchResource(
            baseManifest,
            loadedBranchId,
            branch,
          );
        });

        setBufferPool((prev) => {
          const currentSeries = prev[selectedSeriesId];
          if (!currentSeries) return prev;
          const currentItem = currentSeries.cprCrossSectionImages;
          const currentManifest =
            getCprCrossSectionImagesFromParameter(currentItem?.data) ??
            imageManifest;
          if (
            !currentManifest ||
            currentManifest.revision !== imageManifest.revision
          ) {
            return prev;
          }

          const branchKey = String(loadedBranchId);
          if (currentManifest.branches?.[branchKey]?.loaded === true) {
            return prev;
          }

          const mergedManifest = mergecprCrossSectionImagesBranchResource(
            currentManifest,
            loadedBranchId,
            branch,
          );

          return {
            ...prev,
            [selectedSeriesId]: {
              ...currentSeries,
              cprCrossSectionImages: {
                ...(currentItem ?? {}),
                loaded: true,
                loading: false,
                data: mergedManifest,
              },
            },
          };
        });
      })
      .catch((err) => {
        console.warn("[CPRPanel] failed to load CPR image branch", err);
      });

    return undefined;
  }, [
    accessToken,
    analysisType,
    branchId,
    imageManifest,
    imageManifest?.revision,
    isImageModeEnabled,
    selectedSeriesId,
    setBufferPool,
  ]);
  const [imageAngle, setImageAngle] = useState(0);
  const imageAngleRef = useRef(0);
  const continuousImageAngleRef = useRef(0);
  const wheelDirectionRef = useRef<-1 | 0 | 1>(0);
  const [cprWheelDirection, setCprWheelDirection] = useState<-1 | 0 | 1>(0);

  useEffect(() => {
    imageAngleRef.current = imageAngle;
    continuousImageAngleRef.current = imageAngle;
  }, [imageAngle]);

  const cprImageResourceMode = useMemo<CprMode | null>(() => {
    if (!mode) return null;
    const branchResource = getBranchImageResource(imageManifest, branchId);
    return resolveCprImageResourceModeForAnalysisType({
      analysisType,
      requestedMode: mode,
      cpr: branchResource?.cpr,
    });
  }, [analysisType, branchId, imageManifest, mode]);
  const cprImageResource = useMemo(
    () =>
      cprImageResourceMode
        ? getBranchImageResource(imageManifest, branchId)?.cpr?.[
            cprImageResourceMode
          ]
        : null,
    [branchId, cprImageResourceMode, imageManifest],
  );
  const cprImageFrame = useMemo(
    () =>
      selectNearestCprAngleFrame(
        imageManifest,
        branchId,
        cprImageResourceMode,
        imageAngle,
      ),
    [branchId, cprImageResourceMode, imageAngle, imageManifest],
  );
  const useFocusedStretchedCprInitialViewBox =
    shouldUseFocusedStretchedCprInitialViewBox(analysisType, mode);
  const cprImageInitialViewBoxCandidate = useMemo(() => {
    const defaultViewBox = resolveCprFrameDefaultViewBox(cprImageFrame);
    if (useFocusedStretchedCprInitialViewBox) {
      return resolveLesionStackCprViewBox(cprImageFrame, defaultViewBox);
    }
    return defaultViewBox;
  }, [cprImageFrame, useFocusedStretchedCprInitialViewBox]);
  const cprImageViewFitKey = useFocusedStretchedCprInitialViewBox
    ? isLesionStackViewLayout
      ? "lesion-stack-fit"
      : "stretched-fit"
    : "default";
  const cprImageResetKey = `${imageManifest?.revision ?? "no-revision"}:${analysisTypeKey}:${branchId}:${cprImageResourceMode ?? mode ?? "unknown"}:${cprImageViewFitKey}:${resetref ? "1" : "0"}`;
  const hasFocusedStretchedCprPath = !!cprImageFrame?.centerlinePath?.length;
  const useServerFocusedStretchedCprAnchor =
    useFocusedStretchedCprInitialViewBox &&
    isImageModeEnabled &&
    cprCrossSectionImageMode === "server";
  const cprImageInitialViewBox = useMemo(() => {
    if (!useFocusedStretchedCprInitialViewBox) {
      focusedStretchedCprInitialViewBoxRef.current = null;
      return cprImageInitialViewBoxCandidate;
    }

    if (!hasFocusedStretchedCprPath) {
      return cprImageInitialViewBoxCandidate;
    }

    const cached = focusedStretchedCprInitialViewBoxRef.current;
    if (cached?.resetKey === cprImageResetKey && cached.viewBox) {
      if (useServerFocusedStretchedCprAnchor) {
        return resolveFocusedStretchedCprAnchoredViewBox(
          cprImageFrame,
          cached,
        );
      }
      return alignCprViewBoxToFrameCenter(cprImageFrame, cached.viewBox);
    }

    const baseViewBox = alignCprViewBoxToFrameCenter(
      cprImageFrame,
      cprImageInitialViewBoxCandidate,
    );

    if (cprImageInitialViewBoxCandidate) {
      focusedStretchedCprInitialViewBoxRef.current =
        useServerFocusedStretchedCprAnchor
          ? createFocusedStretchedCprViewBoxCache({
              resetKey: cprImageResetKey,
              frame: cprImageFrame,
              viewBox: baseViewBox,
            })
          : {
              resetKey: cprImageResetKey,
              viewBox: cprImageInitialViewBoxCandidate,
            };
    }

    if (useServerFocusedStretchedCprAnchor) {
      const nextCache = focusedStretchedCprInitialViewBoxRef.current;
      return nextCache?.resetKey === cprImageResetKey
        ? resolveFocusedStretchedCprAnchoredViewBox(cprImageFrame, nextCache)
        : baseViewBox;
    }

    return baseViewBox;
  }, [
    cprCrossSectionImageMode,
    cprImageFrame,
    cprImageInitialViewBoxCandidate,
    cprImageResetKey,
    hasFocusedStretchedCprPath,
    isImageModeEnabled,
    useFocusedStretchedCprInitialViewBox,
    useServerFocusedStretchedCprAnchor,
  ]);
  const cprImageViewportAnchorPoint = useMemo(() => {
    if (
      !useServerFocusedStretchedCprAnchor ||
      !cprImageFrame ||
      !hasFocusedStretchedCprPath
    ) {
      return undefined;
    }

    const cached = focusedStretchedCprInitialViewBoxRef.current;
    const point =
      cached?.resetKey === cprImageResetKey
        ? findFocusedCprAnchorPoint(cprImageFrame, cached)
        : selectFocusedCprAnchorPoint(cprImageFrame)?.point;

    if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) {
      return undefined;
    }

    return { x: point.x, y: point.y };
  }, [
    cprImageFrame,
    cprImageResetKey,
    hasFocusedStretchedCprPath,
    useServerFocusedStretchedCprAnchor,
  ]);

  const cprOverlayLabel = useMemo(
    () => ({
      ...(config.viewOverlayLabels?.cpr ?? config.label),
      lumenoutline: false,
    }),
    [config.label, config.viewOverlayLabels?.cpr],
  );

  const cprImageLayerVisibility = useMemo(() => {
    const next = filterImageLayerVisibilityForAnalysisType(
      analysisType,
      cprOverlayLabel as Partial<Record<ImageLayerKey, boolean>>,
    );

    if (!isLesionStackViewLayout) return next;

    return {
      ...next,
      lumen: false,
      lumenoutline: false,
      plaque: false,
      plaqueoutline: false,
    } as Partial<Record<ImageLayerKey, boolean>>;
  }, [analysisType, cprOverlayLabel, isLesionStackViewLayout]);
  const cprImagePrefetchPlan = useMemo(
    () =>
      buildCprImagePrefetchPlan({
        resource: cprImageResource,
        activeFrame: cprImageFrame,
        revision: imageManifest?.revision,
        visibility: cprImageLayerVisibility,
        direction: cprWheelDirection,
      }),
    [
      cprImageFrame,
      cprImageLayerVisibility,
      cprImageResource,
      cprWheelDirection,
      imageManifest?.revision,
    ],
  );
  const cprImagePrefetchLayers = cprImagePrefetchPlan.layers;
  const rawCprImageLayers = useMemo(
    () =>
      resolveImageLayersWithRevision(
        cprImageFrame?.layers,
        imageManifest?.revision,
      ),
    [cprImageFrame, imageManifest?.revision],
  );
  const shouldUseCprLocalOverlayFallback =
    isCprLocalOverlayFallbackEnabled(config);
  const cprImageLayers = useMemo(
    () =>
      withLocalOverlayFallbackLayers(rawCprImageLayers, {
        seriesId: selectedSeriesId,
        kind: "cpr",
        branchId,
        mode: cprImageResourceMode ?? mode ?? undefined,
        angleDeg: cprImageFrame?.angleDeg,
        enableCprLocalOverlayFallback: shouldUseCprLocalOverlayFallback,
      }),
    [
      branchId,
      cprImageFrame?.angleDeg,
      cprImageResourceMode,
      mode,
      rawCprImageLayers,
      selectedSeriesId,
      shouldUseCprLocalOverlayFallback,
    ],
  );

  useEffect(() => {
    if (!isImageModeEnabled) return;
    if (!cprImageResourceMode) return;

    const angleStartDegrees = Number(cprImageFrame?.angleDeg ?? imageAngle);
    setServerCprImageAngleStartDegrees({
      branchId,
      mode: cprImageResourceMode,
      angleStartDegrees,
    });
  }, [
    branchId,
    cprImageFrame?.angleDeg,
    cprImageResourceMode,
    imageAngle,
    isImageModeEnabled,
  ]);

  const hasReadyCurrentCprImage = !!(cprImageFrame && cprImageLayers?.base);

  const displayedCprImage = hasReadyCurrentCprImage
    ? {
        revision: imageManifest?.revision,
        branchId,
        mode: cprImageResourceMode,
        frame: cprImageFrame,
        layers: cprImageLayers,
        initialViewBox: cprImageInitialViewBox,
        viewportAnchorPoint: cprImageViewportAnchorPoint,
        resetKey: cprImageResetKey,
      }
    : null;
  const displayedBranchId = branchId;
  const displayedInfo = panelState.state[displayedBranchId as any] ?? [];
  const isWaitingForSelectedBranchImage =
    isImageModeEnabled && !hasReadyCurrentCprImage;
  const cprImageOverlayPolicy = useMemo(
    () => ({
      analysisType: isAorticMode ? ("aorticValveCalcium" as any) : analysisType,
      config: config as Record<string, any>,
    }),
    [analysisType, config, isAorticMode],
  );
  const cprImageCacheScope = useMemo(
    () => ({
      seriesId: selectedSeriesId,
      revision: imageManifest?.revision,
      branchId,
      view: "cpr",
      mode: cprImageResourceMode ?? mode ?? undefined,
      resourceMode: cprCrossSectionImageMode,
    }),
    [
      branchId,
      cprCrossSectionImageMode,
      cprImageResourceMode,
      imageManifest?.revision,
      mode,
      selectedSeriesId,
    ],
  );
  const cprImageDiagnostic = useMemo(() => {
    if (!cprImageFrame) return undefined;
    const path = cprImageFrame.centerlinePath ?? [];
    const firstPathIndex = path[0]?.index;
    const lastPathIndex = path[path.length - 1]?.index;
    return [
      `strategy=${cprImagePrefetchPlan.strategy}`,
      `prefetch=${cprImagePrefetchPlan.selectedCount}/${cprImagePrefetchPlan.candidateCount}`,
      `estimate=${cprImagePrefetchPlan.estimatedDecodedBytes}`,
      `path=${firstPathIndex ?? "n/a"}-${lastPathIndex ?? "n/a"}`,
      `local=${cprImageFrame.localIndexStart ?? "n/a"}-${cprImageFrame.localIndexEnd ?? "n/a"}`,
      `source=${cprImageFrame.sourceIndexStart ?? "n/a"}-${cprImageFrame.sourceIndexEnd ?? "n/a"}`,
      `padding=${cprImageFrame.frontPaddingRows ?? 0}/${cprImageFrame.backPaddingRows ?? 0}`,
      `validRows=${cprImageFrame.validImageRowStart ?? "n/a"}-${cprImageFrame.validImageRowEnd ?? "n/a"}`,
    ].join(";");
  }, [cprImageFrame, cprImagePrefetchPlan]);
  const hasLabelData = pool?.labelData.loaded;
  const hasCenterData =
    pool?.centerData.loaded &&
    !!(pool.centerData.data as Record<number, vtkPolyData>)[branchId];
  const hasBranchGraphDataset = pool?.branchGraphDataset.loaded;
  const branchGraphDataset = pool?.branchGraphDataset.data as
    | BranchInfo
    | undefined;
  const lesionStackLengthArray = branchGraphDataset?.getLengthArray(
    displayedBranchId as any,
  );

  useEffect(() => {
    const shouldKeepCurrentMiddle = (
      branchKey: string,
      savedMiddle: LesionStackSavedMiddle,
    ) => {
      if (!useServerLesionStackHiddenMiddleSyncRef.current) return false;

      const currentStates = panelStateStateRef.current[Number(branchKey)] ?? [];
      const currentProximalIndex = Number(currentStates[0]?.index);
      const currentDistalIndex = Number(currentStates[2]?.index);

      return (
        Number.isFinite(currentProximalIndex) &&
        Number.isFinite(currentDistalIndex) &&
        (Math.round(currentProximalIndex) !==
          Math.round(savedMiddle.proximalIndex) ||
          Math.round(currentDistalIndex) !== Math.round(savedMiddle.distalIndex))
      );
    };

    const restoreSavedMiddle = (branchKey: string | null) => {
      if (!branchKey) return;
      const savedMiddle = lesionStackSavedMiddleRef.current[branchKey];
      if (!savedMiddle) return;

      if (!shouldKeepCurrentMiddle(branchKey, savedMiddle)) {
        updateCPRState({
          indexedState: {
            mainIndex: {
              branchId: Number(branchKey),
              index: 1,
            },
            pickPoint: savedMiddle.pickPoint,
          },
        });
      }

      delete lesionStackSavedMiddleRef.current[branchKey];
    };

    if (!isLesionStackViewLayout || isAorticMode) {
      restoreSavedMiddle(activeLesionStackBranchRef.current);
      activeLesionStackBranchRef.current = null;
      return;
    }

    if (displayedBranchId === undefined || displayedBranchId === null) return;
    if (!displayedInfo?.length || displayedInfo.length < 3) return;
    if (!lesionStackLengthArray?.length) return;

    const branchKey = String(displayedBranchId);
    if (
      activeLesionStackBranchRef.current &&
      activeLesionStackBranchRef.current !== branchKey
    ) {
      restoreSavedMiddle(activeLesionStackBranchRef.current);
    }
    activeLesionStackBranchRef.current = branchKey;

    if (!lesionStackSavedMiddleRef.current[branchKey]) {
      lesionStackSavedMiddleRef.current[branchKey] = {
        pickPoint: { ...(displayedInfo[1] ?? { index: -1 }) },
        proximalIndex: Number(displayedInfo[0]?.index),
        distalIndex: Number(displayedInfo[2]?.index),
      };
      const defaultMiddleAnchorIndex = getLesionStackDefaultMiddleAnchorIndex({
        pickPoints: displayedInfo,
        lengthArray: lesionStackLengthArray,
        frontExpand: config.coronary_front_expand,
        spacingMm: lesionStackSpacingMm,
      });
      if (Number.isFinite(defaultMiddleAnchorIndex)) {
        updateCPRState({
          indexedState: {
            mainIndex: {
              branchId: Number(branchKey),
              index: 1,
            },
            pickPoint: { index: defaultMiddleAnchorIndex },
          },
        });
      }
    }
  }, [
    config.coronary_front_expand,
    displayedBranchId,
    displayedInfo?.length,
    displayedInfo?.[0]?.index,
    displayedInfo?.[1]?.index,
    displayedInfo?.[2]?.index,
    isAorticMode,
    isLesionStackViewLayout,
    lesionStackLengthArray,
    lesionStackSpacingMm,
    updateCPRState,
  ]);

  useEffect(
    () => () => {
      const branchKey = activeLesionStackBranchRef.current;
      if (!branchKey) return;
      const savedMiddle = lesionStackSavedMiddleRef.current[branchKey];
      if (!savedMiddle) return;

      const currentStates = panelStateStateRef.current[Number(branchKey)] ?? [];
      const currentProximalIndex = Number(currentStates[0]?.index);
      const currentDistalIndex = Number(currentStates[2]?.index);
      const markerBoundsChanged =
        useServerLesionStackHiddenMiddleSyncRef.current &&
        Number.isFinite(currentProximalIndex) &&
        Number.isFinite(currentDistalIndex) &&
        (Math.round(currentProximalIndex) !==
          Math.round(savedMiddle.proximalIndex) ||
          Math.round(currentDistalIndex) !== Math.round(savedMiddle.distalIndex));

      if (!markerBoundsChanged) {
        updateCPRState({
          indexedState: {
            mainIndex: {
              branchId: Number(branchKey),
              index: 1,
            },
            pickPoint: savedMiddle.pickPoint,
          },
        });
      }

      delete lesionStackSavedMiddleRef.current[branchKey];
      activeLesionStackBranchRef.current = null;
    },
    [updateCPRState],
  );

  const lesionStackMarkerIndexes = useMemo(() => {
    if (!isLesionStackViewLayout || isAorticMode) return [];
    return buildLesionStackMarkerIndexes({
      pickPoints: displayedInfo,
      lengthArray: lesionStackLengthArray,
      frontExpand: config.coronary_front_expand,
      spacingMm: lesionStackSpacingMm,
    });
  }, [
    config.coronary_front_expand,
    displayedBranchId,
    displayedInfo[0]?.index,
    displayedInfo[1]?.index,
    displayedInfo[2]?.index,
    isAorticMode,
    isLesionStackViewLayout,
    lesionStackLengthArray,
    lesionStackSpacingMm,
  ]);
  const visibleLesionStackMarkerIndexes = useMemo(
    () =>
      lesionStackMarkerIndexes.filter((index): index is number =>
        Number.isFinite(index),
      ),
    [lesionStackMarkerIndexes],
  );
  const hasAorticVisualDataReady = !isAorticMode
    ? true
    : (config.aorta_indexs?.length ?? 0) > 0 ||
      (config.avc_indexs?.length ?? 0) > 0;

  const isDataReady =
    !!hasLabelData &&
    !!hasCenterData &&
    (isAorticMode ? true : !!hasBranchGraphDataset) &&
    hasAorticVisualDataReady;

  const isBoundaryEditCpr =
    mode === "straightened" &&
    (config.EditMode === EditNames.lumen ||
      config.EditMode === EditNames.plaque);

  const { windowLevel, windowWidth, invert } = currentVOI;
  const cprImageWindowingPolicy = useMemo<ImageWindowingPolicy>(
    () => ({
      windowLevel,
      windowWidth,
      invert,
    }),
    [invert, windowLevel, windowWidth],
  );
  const effectiveCprImageActiveToolId =
    panelToolState?.activeToolId ?? selectedTool?.id;

  const handleCprImageWindowLevelChange = useCallback(
    (next: ImageWindowingPolicy) => {
      updateWindowLevelWidth([next.windowLevel, next.windowWidth]);
    },
    [updateWindowLevelWidth],
  );

  useLayoutEffect(() => {
    if (!isImageModeEnabled || !selectedSeriesId) return;
    const labelVolume = getLabelVolumeFromSeriesId(
      selectedSeriesId,
      config.generalSetting?.renderingQuality !== "high",
    );
    const huVolume = getVolumeFromSeriesId(
      selectedSeriesId,
      config.generalSetting?.renderingQuality !== "high",
    );
    registerLocalOverlayImageContext(selectedSeriesId, {
      analysisType,
      config,
      currentVOI,
      pool,
      huImageData: huVolume?.imageData as any,
      labelDataByBranch: pool?.labelData.data as
        | Record<number, any>
        | undefined,
      centerDataByBranch: pool?.centerData.data as
        | Record<number, vtkPolyData>
        | undefined,
      aorticLabelData: labelVolume?.imageData as any,
      forceCprLocalOverlayFallback: false,
    });
  }, [
    analysisType,
    branchGraphDataset,
    config,
    currentVOI,
    cprCrossSectionImageMode,
    isImageModeEnabled,
    pool,
    selectedSeriesId,
  ]);

  useEffect(() => {
    imageAngleRef.current = 0;
    continuousImageAngleRef.current = 0;
    wheelDirectionRef.current = 0;
    setCprWheelDirection(0);
    setImageAngle(0);
  }, [branchId, imageManifest?.revision, mode]);

  useEffect(() => {
    if (!isImageModeEnabled) return;
    if (analysisType !== "aorticValveCalcium") return;
    if (!isDataReady) return;

    const currentMarkerIndex = Number(info[0]?.index);
    if (Number.isFinite(currentMarkerIndex) && currentMarkerIndex >= 0) {
      const configIndex = Number(aorticCurrentIndex);
      if (currentMarkerIndex !== configIndex) {
        setData({ aorta_current_index: currentMarkerIndex });
      }
      return;
    }

    const nextIndex = [
      aorticCurrentIndex,
      aorticStartIndex,
      cprImageFrame?.centerlinePath?.[0]?.index,
    ]
      .map((value) => Number(value))
      .find((value) => Number.isFinite(value) && value >= 0);
    if (nextIndex === undefined) return;
    if (info[0]?.index === nextIndex) return;

    setData({ aorta_current_index: nextIndex });
    updateCPRState({
      indexedStates: {
        [branchId]: [{ index: nextIndex }],
      },
    });
    scheduleRender(`CrossSection.${mainIndex}.0`, {
      type: "markerMove",
      which: 0,
      newIndex: nextIndex,
    });
  }, [
    analysisType,
    aorticCurrentIndex,
    aorticStartIndex,
    branchId,
    cprImageFrame?.centerlinePath,
    info[0]?.index,
    isDataReady,
    isImageModeEnabled,
    mainIndex,
    setData,
    updateCPRState,
  ]);

  useLayoutEffect(() => {
    if (!isImageModeEnabled || !rendererId) return;

    const rendererEntry = {
      render: async (args?: any) => {
        const { type, realIdx, which } = args ?? {};
        const rawPickedPoints = Array.isArray(args?.newPickedPoints)
          ? args.newPickedPoints.filter(Boolean)
          : [];
        const fallbackIndex = Number.isFinite(args?.newIndices)
          ? args.newIndices
          : Number.isFinite(args?.index)
            ? args.index
            : Number.isFinite(args?.newIndex)
              ? args.newIndex
              : null;
        const newPickedPoints =
          rawPickedPoints.length > 0
            ? rawPickedPoints
            : fallbackIndex !== null
              ? [{ index: fallbackIndex }]
              : [];

        switch (type) {
          case "reset":
            imageAngleRef.current = 0;
            continuousImageAngleRef.current = 0;
            setImageAngle(0);
            setresetref((prev) => !prev);
            break;
          case "markerMove":
          case "polygonMove":
            if (
              !Array.isArray(newPickedPoints) ||
              newPickedPoints.length === 0
            ) {
              break;
            }
            if (newPickedPoints.length !== 1) {
              const states = panelState.state[branchId] ?? [];
              const baseIdx = Number.isFinite(realIdx) ? realIdx : 0;
              updateCPRState({
                indexedStates: {
                  [branchId]: [
                    ...states.slice(0, baseIdx * 3),
                    ...newPickedPoints,
                    ...states.slice((baseIdx + 1) * 3),
                  ],
                },
              });
            } else if (Number.isFinite(which)) {
              updateCPRState({
                indexedState: {
                  mainIndex: {
                    branchId,
                    index: which,
                  },
                  pickPoint: newPickedPoints[0],
                },
              });
            }
            break;
          default:
            break;
        }
      },
    };

    return registerRenderer(rendererId, rendererEntry);
  }, [branchId, isImageModeEnabled, panelState, rendererId, updateCPRState]);

  const handleCprImageWheel = useCallback(
    (event: ImageLayerStackWheelEvent) => {
      if (event.ctrlKey) return;
      if (event.cancelable) event.preventDefault();

      const deltaY = Number(event.deltaY);
      if (!Number.isFinite(deltaY) || deltaY === 0) return;

      const nextDirection: -1 | 1 = deltaY > 0 ? 1 : -1;
      if (wheelDirectionRef.current !== nextDirection) {
        wheelDirectionRef.current = nextDirection;
        setCprWheelDirection(nextDirection);
      }

      // Keep a continuous angle accumulator so small trackpad deltas are not
      // lost, but commit the snapped frame immediately.  The previous RAF
      // coalescing added one more frame of latency on top of ImageLayerStack's
      // async resolve path, which made fast wheel input feel like it was
      // trailing behind even when the image cache was hot.
      continuousImageAngleRef.current = normalizeAngleDeg(
        continuousImageAngleRef.current + deltaY * CPR_IMAGE_WHEEL_ANGLE_FACTOR,
      );
      const next = snapCprServerImageAngle(continuousImageAngleRef.current);
      if (next === imageAngleRef.current) return;

      imageAngleRef.current = next;
      setImageAngle(next);
    },
    [],
  );

  const handleCprImageScrollDrag = useCallback(
    (deltaX: number, deltaY: number) => {
      const primaryDelta =
        Math.abs(deltaX) >= Math.abs(deltaY) ? deltaX : deltaY;
      if (!Number.isFinite(primaryDelta) || primaryDelta === 0) return;

      // Match the legacy CPR drag feel while also supporting vertical drag
      // gestures in the server-image shell. Reuse the continuous accumulator so
      // drag updates stay responsive even when the snapped 5-degree frame does
      // not change on every pointermove.
      continuousImageAngleRef.current = normalizeAngleDeg(
        continuousImageAngleRef.current +
          primaryDelta * CPR_IMAGE_SCROLL_DRAG_ANGLE_FACTOR,
      );
      const next = snapCprServerImageAngle(continuousImageAngleRef.current);
      if (next === imageAngleRef.current) return;

      imageAngleRef.current = next;
      setImageAngle(next);
    },
    [],
  );

  useCprRendererLifecycle({
    ref,
    context,
    rendererId,
    analysisType,
    contextPool: contextPool as Partial<Record<string, CPRContext | null>>,
    setContextPool,
    disabled: isImageModeEnabled,
  });

  const applyBoundaryEditActorVisibility = useCprPaintActorVisibility({
    context,
    isBoundaryEditCpr,
  });

  useCprInitialMarkerReset({
    resetref,
    info,
    analysisType,
    branchId,
    pool,
    config,
    updateCPRState,
    setData,
    setresetref,
    aorticStartIndex,
  });

  useCprFaiInitialization({
    analysisType,
    branchGraphDataset,
    resetref,
    mainBranchIds,
    config,
    updateCPRState,
    mainIndex,
    pool,
    setresetref,
  });

  useCprDataRenderer({
    context,
    selectedSeriesId,
    isDataReady,
    hasAorticVisualDataReady,
    mode,
    analysisType,
    branchIds,
    branchId,
    aorticBranchId,
    aorticCurrentIndex,
    panelState,
    updateCPRState,
    config,
    currentVOI,
    pool,
    rendererId,
    dimensions,
    refSetter,
    gogo,
    setGogo,
    setresetref,
    applyBoundaryEditActorVisibility,
    switchViewReport: switchConfigs.switchViewReport,
    disabled: isImageModeEnabled,
  });

  useCprAnglePathRefresh({
    context,
    analysisType,
    disabled: isImageModeEnabled,
  });

  useCprInteractionEvents({
    ref,
    context,
    analysisType,
    panelToolState,
    currentVOI,
    updateWindowLevelWidth,
    config,
    onCprOverlayChange: externalFocusIndicator.visible
      ? forceUpdateCprOverlay
      : undefined,
    disabled: isImageModeEnabled,
  });

  useCprPanelDimensions(ref, setDimensions);

  useCprWindowLevelEffect({
    context,
    rendererId,
    windowLevel,
    windowWidth,
    invert,
  });

  useCprRendererRegistration({
    rendererId,
    context,
    analysisType,
    contextPool,
    mainIndex,
    mode,
    branchId,
    panelState,
    updateCPRState,
    refSetter,
    setGogo,
    setresetref,
    setlabelonoff,
    setExternalFocusIndicator,
    applyBoundaryEditActorVisibility,
    overlayLabel: cprOverlayLabel,
    forceLabelVisibilityOff: isLesionStackViewLayout,
    disabled: isImageModeEnabled,
  });

  const {
    open,
    annoopen,
    handlePopupClose,
    handleAnnoPopupClose,
    projectionContextMenuItems,
    bottomLeftContent,
    bottomRightContent,
    annotationMenuItems,
  } = useCprAnnotationMenus({
    analysisType,
    branchIds,
    branchId,
    mainIndex,
    panelConfig,
    config,
    setData,
    contextPool,
    updateCPRState,
    labelonoff,
    isLesionStackViewLayout,
  });

  useCprResetOnResize({ context, dimensions });

  const memoizedClassName = useMemo(() => {
    const isFullscreen =
      fullscreenPanelComponentKey === panelConfig.componentKey;
    const isSelected = selectedPanelClassName === className;
    return `panel ${className}${isFullscreen && isSelected ? " fullscreen" : ""}`;
  }, [
    fullscreenPanelComponentKey,
    className,
    selectedPanelClassName,
    panelConfig.componentKey,
  ]);

  const classNameWithBranchName = useMemo(
    () =>
      `renderer CPR ${mode} ${(branchIds ?? [])
        .filter((val) => !!val[branchId])
        .flatMap((v) => Object.values(v)[0])
        .join("-")}-${mainIndex}`,
    [mode, branchIds, branchId, mainIndex],
  );

  const numPairs =
    analysisType === "aorticValveCalcium" || analysisTypeKey === "tavi" ? 1 : 3;

  useEffect(() => {
    if (!externalFocusIndicator.visible) return undefined;

    const timer = window.setTimeout(() => {
      setExternalFocusIndicator({ visible: false, branchId: null, index: -1 });
    }, 5000);

    return () => window.clearTimeout(timer);
  }, [
    externalFocusIndicator.visible,
    externalFocusIndicator.branchId,
    externalFocusIndicator.index,
  ]);

  const closeCprPopupMenus = useCallback(() => {
    if (open["projection"]) {
      handlePopupClose("projection");
    }
    if (annoopen["projection"]) {
      handleAnnoPopupClose("projection");
    }
  }, [annoopen, handleAnnoPopupClose, handlePopupClose, open]);

  const closeCprPopupMenusOnPanelMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest(".base-context-menu")) return;
      closeCprPopupMenus();
    },
    [closeCprPopupMenus],
  );

  useEffect(() => {
    if (!open["projection"] && !annoopen["projection"]) return;

    const handleDocumentPointerDown = (event: PointerEvent | MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (target.closest(".base-context-menu")) return;
      if (!ref.current?.contains(target)) return;
      closeCprPopupMenus();
    };

    // The context menu is rendered through a portal, and some image-mode
    // handlers stop bubbling inside the CPR viewport. Capture phase keeps the
    // overlay menu behavior identical to the legacy VTK view: clicking the CPR
    // image itself closes the Overlays/branch menu.
    document.addEventListener("pointerdown", handleDocumentPointerDown, true);
    document.addEventListener("mousedown", handleDocumentPointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", handleDocumentPointerDown, true);
      document.removeEventListener("mousedown", handleDocumentPointerDown, true);
    };
  }, [annoopen, closeCprPopupMenus, open]);

  const cprExternalFocusIndicatorY = useMemo(() => {
    if (!externalFocusIndicator.visible) return null;
    if (config.EditMode !== EditNames.lumen) return null;
    if (mode !== "straightened") return null;
    if (
      externalFocusIndicator.branchId !== null &&
      externalFocusIndicator.branchId !== branchId
    ) {
      return null;
    }

    const current = context.current;
    const cprPath = current?.path as
      | Array<{ x: number; y: number }>
      | undefined;
    if (!cprPath?.length) return null;

    const index = Math.max(
      0,
      Math.min(cprPath.length - 1, Math.round(externalFocusIndicator.index)),
    );
    const point = cprPath[index];
    if (!point || !Number.isFinite(point.y)) return null;

    const overlayY = current?.overlayTranslation?.[1] ?? 0;
    return point.y + overlayY;
  }, [
    branchId,
    config.EditMode,
    externalFocusIndicator.branchId,
    externalFocusIndicator.index,
    externalFocusIndicator.visible,
    cprViewRevision,
    mode,
  ]);

  return (
    <div
      className={memoizedClassName}
      onMouseDownCapture={(event) => {
        closeCprPopupMenusOnPanelMouseDown(event);
        setActiveCprBoundaryEditHistoryKey(rendererId);
        updateSelectedPanelConfig({
          ...panelConfig,
          componentKey: panelConfig.componentKey,
          className,
        });
      }}
    >
      <div className={classNameWithBranchName} ref={ref}>
        {isImageModeEnabled ? (
          <ImageLayerStack
            layers={displayedCprImage?.layers ?? null}
            revision={displayedCprImage?.revision ?? imageManifest?.revision}
            width={displayedCprImage?.frame.width ?? Math.max(1, dimensions.width)}
            height={displayedCprImage?.frame.height ?? Math.max(1, dimensions.height)}
            seriesId={selectedSeriesId}
            accessToken={accessToken}
            prefetchLayers={cprImagePrefetchLayers}
            prefetchConcurrency={4}
            priority={40}
            loading={isWaitingForSelectedBranchImage}
            disableStaleFallback={isWaitingForSelectedBranchImage}
            fitMode="viewBoxContain"
            initialViewBox={displayedCprImage?.initialViewBox}
            viewportAnchorPoint={displayedCprImage?.viewportAnchorPoint}
            resetKey={displayedCprImage?.resetKey ?? cprImageResetKey}
            enableViewportTransform
            activeToolId={effectiveCprImageActiveToolId}
            visibility={cprImageLayerVisibility}
            overlayPolicy={cprImageOverlayPolicy}
            windowingPolicy={cprImageWindowingPolicy}
            cacheScope={cprImageCacheScope}
            diagnosticLabel={isWaitingForSelectedBranchImage ? `waiting-branch=${branchId};${cprImageDiagnostic ?? ""}` : cprImageDiagnostic}
            onWheel={handleCprImageWheel}
            onScrollDrag={handleCprImageScrollDrag}
            onWindowLevelChange={handleCprImageWindowLevelChange}
            viewportOverlay={(viewportContext) =>
              displayedCprImage?.frame && displayedInfo && !!displayedInfo.length && displayedInfo[0].index !== -1 ? (
                <CprImageMarkerOverlay
                  width={displayedCprImage.frame.width}
                  height={displayedCprImage.frame.height}
                  mode={mode!}
                  mainIndex={mainIndex}
                  branchId={displayedBranchId as any}
                  pickPoints={displayedInfo}
                  numPairs={numPairs}
                  centerlinePath={displayedCprImage.frame.centerlinePath}
                  overlayGeometry={displayedCprImage.frame.overlayGeometry}
                  imageManifest={imageManifest}
                  viewportContext={viewportContext}
                  hidePlaqueOutline={isLesionStackViewLayout}
                  hidePlaqueLesion={isLesionStackViewLayout}
                  stackMarkerIndexes={
                    isLesionStackViewLayout ? visibleLesionStackMarkerIndexes : undefined
                  }
                  isServerImageMode={cprCrossSectionImageMode === "server"}
                  overlayLabel={cprOverlayLabel}
                  centerlineData={
                    (
                      pool?.centerData.data as
                        | Record<number, vtkPolyData>
                        | undefined
                    )?.[displayedBranchId as any]
                  }
                />
              ) : null
            }
          />
        ) : (
          info &&
          !!info.length &&
          info[0].index !== -1 &&
          pool?.centerData.loaded &&
          context.current?.path && (
            <MarkerLine
              key={classNameWithBranchName}
              mode={mode!}
              mainIndex={mainIndex}
              centerlineData={
                (context.current?.centerlineData as vtkPolyData | undefined) ??
                (pool?.centerData.data as Record<number, vtkPolyData>)[branchId]
              }
              dimensions={dimensions}
              branchId={branchId}
              pickPoints={info}
              numPairs={numPairs}
              context={context.current}
            />
          )
        )}
        {cprExternalFocusIndicatorY !== null && (
          <svg
            width={dimensions.width}
            height={dimensions.height}
            viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              zIndex: 20,
            }}
          >
            <g pointerEvents="none">
              <line
                x1={24}
                y1={cprExternalFocusIndicatorY}
                x2={Math.max(dimensions.width - 24, 24)}
                y2={cprExternalFocusIndicatorY}
                stroke="#2ee6ff"
                strokeWidth={2}
                strokeDasharray="4 4"
                opacity={0.35}
              />
            </g>
          </svg>
        )}
      </div>
      {globalToolState?.toggles.ToggleAnnotations && (
        <PanelOverlay
          config={{
            "bottom-left": {
              content: bottomLeftContent,
            },
            "bottom-right": {
              content: bottomRightContent,
            },
          }}
          originComponentKey={panelConfig.componentKey}
          className={className}
        />
      )}

      {(analysisType === "plaque" ||
        analysisType === "isr" ||
        analysisType === "stenosis") &&
        open["projection"] && (
          <SimpleContextMenu
            items={projectionContextMenuItems}
            onClose={() => handlePopupClose("projection")}
            checkbox={true}
          />
        )}

      {annoopen["projection"] && (
        <SimpleContextMenu
          items={annotationMenuItems}
          onClose={() => handleAnnoPopupClose("projection")}
          checkbox={true}
        />
      )}
    </div>
  );
};

export default CPRPanel;
